// src/pages/ClientPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  InputGroup,
  Pagination,
  Badge,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ProtectedSection from '../components/ProtectedSection';
import clientService from '../services/clientService';
import clientMinorService from '../services/clientMinorService';
import ClientModal from '../components/ClientModal';
import ClientFilesModal from '../components/ClientFilesModal';
import ClientExtraFieldsModal from '../components/ClientExtraFieldsModal';
import ClientMinorListModal from '../components/ClientMinorListModal';
import ClientMinorCreateFromPickerModal from '../components/ClientMinorCreateFromPickerModal';
import ClientViewModal from '../components/ClientViewModal';
import ClientMinorViewModal from '../components/ClientMinorViewModal';

const normalize = (str) => {
  if (str == null) return '';
  return String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

const toText = (v) => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(toText).join(' ');
  if (typeof v === 'object') return Object.values(v).map(toText).join(' ');
  return String(v);
};

export default function ClientPage() {
  const [adults, setAdults] = useState([]);
  const [minors, setMinors] = useState([]);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const [filesModal, setFilesModal] = useState({ show: false, clientId: null });
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [minorsModal, setMinorsModal] = useState({ show: false, parent: null });
  const [showCreateMinorModal, setShowCreateMinorModal] = useState(false);

  const [minorEditPicker, setMinorEditPicker] = useState({
    show: false,
    initial: null,
  });

  const [viewAdult, setViewAdult] = useState(null);
  const [viewMinor, setViewMinor] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const navigate = useNavigate();

  const fetch = async () => {
    try {
      const [adultList, minorList] = await Promise.all([
        clientService.list(),
        clientMinorService.listAll(),
      ]);
      setAdults(adultList);
      setMinors(minorList);
    } catch (err) {
      if (err.response?.status === 403) navigate('/login');
    }
  };

  useEffect(() => {
    fetch();
  }, []); // eslint-disable-line

  const combined = useMemo(() => {
    const A = (adults || []).map((a) => ({ __type: 'adult', ...a }));
    const M = (minors || []).map((m) => ({
      __type: 'minor',
      id: m.id,
      firstName: m.firstName,
      lastName: m.lastName,
      nifCin: '—',
      email: m.email || null,
      phone: m.phone || null,
      active: typeof m.active === 'boolean' ? m.active : true,
      parentId: m.parentId || (m.parent?.id ?? null),
      parent: m.parent || null,
      _parentName: m.parent ? `${m.parent.firstName} ${m.parent.lastName}` : '',
      _relation: m.relation || '',
    }));
    return [...A, ...M];
  }, [adults, minors]);

  useEffect(() => {
    setPage(1);
  }, [filter, combined]);

  const filtered = useMemo(() => {
    const q = normalize(filter);
    if (!q) return combined;
    return combined.filter((c) => {
      const haystack = normalize(
        [
          c.firstName,
          c.lastName,
          c.nifCin,
          c.email,
          c.phone,
          c.profession,
          c.address,
          c.active === false ? 'desactive inactif désactivé' : 'actif active activé',
          c.__type === 'minor' ? 'oui mineur' : 'non majeur adulte',
          c._parentName,
          c._relation,
        ]
          .map(toText)
          .join(' ')
      );
      return haystack.includes(q);
    });
  }, [combined, filter]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;
  const visible = useMemo(
    () => filtered.slice(start, start + pageSize),
    [filtered, start, pageSize]
  );

  const handleCreate = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleEdit = (client) => {
    clientService
      .get(client.id)
      .then((full) => {
        setEditData(full);
        setShowModal(true);
      })
      .catch((err) => {
        if (err.response?.status === 403) navigate('/login');
      });
  };

  const handleDelete = (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    clientService.remove(id).then(fetch);
  };

  const handleSave = (data) => {
    const id = editData?.id;
    const op = id ? clientService.update(id, data) : clientService.create(data);
    return op.then(() => {
      setShowModal(false);
      fetch();
    });
  };

  const handleActivateAdult = async (id) => {
    try {
      await clientService.activate(id);
      fetch();
    } catch (e) {
      console.error(e);
      alert("Impossible d'activer ce client.");
    }
  };

  const handleDeactivateAdult = async (id) => {
    try {
      await clientService.deactivate(id);
      fetch();
    } catch (e) {
      console.error(e);
      alert("Impossible de désactiver ce client.");
    }
  };

  const handleActivateMinor = async (id) => {
    try {
      await clientMinorService.activate(id);
      fetch();
    } catch (e) {
      console.error(e);
      alert("Impossible d'activer ce compte mineur.");
    }
  };

  const handleDeactivateMinor = async (id) => {
    try {
      await clientMinorService.deactivate(id);
      fetch();
    } catch (e) {
      console.error(e);
      alert("Impossible de désactiver ce compte mineur.");
    }
  };

  const handleViewAdult = async (row) => {
    try {
      const full = await clientService.get(row.id);
      setViewAdult(full);
    } catch {
      alert('Impossible de charger le client.');
    }
  };

  const handleMinorEdit = async (minorRow) => {
    try {
      const full = await clientMinorService.get(minorRow.id);
      setMinorEditPicker({ show: true, initial: full });
    } catch {
      alert('Impossible de charger le compte mineur.');
    }
  };

  const handleMinorDelete = (minorRow) => {
    if (
      !window.confirm(
        `Supprimer le compte mineur ${minorRow.firstName} ${minorRow.lastName} ?`
      )
    )
      return;
    clientMinorService.remove(minorRow.id).then(fetch);
  };

  const handleMinorPickerSaveEdit = async ({ parentId, fields, id }) => {
    await clientMinorService.update(id, { ...fields, parentId });
    setMinorEditPicker({ show: false, initial: null });
    fetch();
  };

  const handleViewMinor = async (minorRow) => {
    try {
      const full = await clientMinorService.get(minorRow.id);
      setViewMinor(full);
    } catch {
      alert('Impossible de charger le compte mineur.');
    }
  };

  const renderPagination = () => (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <div className="text-muted">
        Résultats : {total} • Page {current}/{totalPages}
      </div>
      <div className="d-flex align-items-center">
        <Form.Select
          size="sm"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="me-2"
          style={{ width: 90 }}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </Form.Select>
        <Pagination className="mb-0">
          <Pagination.Prev
            disabled={current === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          />
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - current) <= 1)
            .map((p, idx, arr) => {
              const prev = arr[idx - 1];
              const needEllipsis = prev && p - prev > 1;
              return (
                <React.Fragment key={p}>
                  {needEllipsis && <Pagination.Ellipsis disabled />}
                  <Pagination.Item active={p === current} onClick={() => setPage(p)}>
                    {p}
                  </Pagination.Item>
                </React.Fragment>
              );
            })}
          <Pagination.Next
            disabled={current === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </Pagination>
      </div>
    </div>
  );

  return (
    <>
      <Container fluid className="mt-4">
        <Row className="mb-3 align-items-center">
          <Col md={4}>
            <h3 className="fw-semibold">Gestion des clients</h3>
          </Col>

          <Col md={4}>
            <InputGroup>
              <Form.Control
                placeholder="Rechercher (nom, nif, téléphone, email, statut...)"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              {filter && (
                <Button variant="outline-secondary" onClick={() => setFilter('')}>
                  ×
                </Button>
              )}
            </InputGroup>
          </Col>

          <Col md={4} className="text-end">
            <ProtectedSection code="CLIENT_CREATE">
              <Button variant="primary" className="me-2" onClick={handleCreate}>
                + Créer un client
              </Button>
            </ProtectedSection>

            <ProtectedSection code="CLIENT_MINOR_CREATE">
              <Button
                variant="outline-success"
                className="me-2"
                onClick={() => setShowCreateMinorModal(true)}
              >
                + Compte mineur
              </Button>
            </ProtectedSection>

            <ProtectedSection code="CLIENT_FIELD_VIEW">
              <Button
                variant="outline-dark"
                onClick={() => setShowFieldsModal(true)}
              >
                Champs extra
              </Button>
            </ProtectedSection>
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Nom complet</th>
              <th>NIF/CIN</th>
              <th>Téléphone</th>
              <th>Email</th>
              <th>Statut</th>
              <th style={{ minWidth: 430 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={`${row.__type}-${row.id}`}>
                <td>
                  {row.__type === 'adult' ? (
                    <Badge bg="info" text="dark">
                      Adulte
                    </Badge>
                  ) : (
                    <Badge bg="warning" text="dark">
                      Mineur
                    </Badge>
                  )}
                </td>
                <td>
                  {row.firstName} {row.lastName}
                  {row.__type === 'minor' && row._parentName ? (
                    <div className="small text-muted">
                      Parent : {row._parentName}
                    </div>
                  ) : null}
                </td>
                <td>{row.nifCin || '—'}</td>
                <td>{row.phone || '—'}</td>
                <td>{row.email || '—'}</td>
                <td>
                  {row.active ? (
                    <Badge bg="success">Actif</Badge>
                  ) : (
                    <Badge bg="danger">Désactivé</Badge>
                  )}
                </td>
                <td>
                  {row.__type === 'adult' ? (
                    <>
                      <ProtectedSection code="CLIENT_VIEW">
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => handleViewAdult(row)}
                        >
                          Voir
                        </Button>
                      </ProtectedSection>

                      <ProtectedSection code="CLIENT_UPDATE">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => handleEdit(row)}
                        >
                          Modifier
                        </Button>
                      </ProtectedSection>

                      <ProtectedSection code="CLIENT_DELETE">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => handleDelete(row.id)}
                        >
                          Supprimer
                        </Button>
                      </ProtectedSection>

                      <ProtectedSection code="CLIENT_FILE_VIEW">
                        <Button
                          variant="outline-dark"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() =>
                            setFilesModal({ show: true, clientId: row.id })
                          }
                        >
                          Fichiers
                        </Button>
                      </ProtectedSection>

                      <ProtectedSection code="CLIENT_MINOR_VIEW">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() =>
                            setMinorsModal({ show: true, parent: row })
                          }
                        >
                          Mineurs
                        </Button>
                      </ProtectedSection>

                      {row.active ? (
                        <ProtectedSection code="CLIENT_DEACTIVATE">
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="mb-1"
                            onClick={() => handleDeactivateAdult(row.id)}
                          >
                            Désactiver
                          </Button>
                        </ProtectedSection>
                      ) : (
                        <ProtectedSection code="CLIENT_ACTIVATE">
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="mb-1"
                            onClick={() => handleActivateAdult(row.id)}
                          >
                            Activer
                          </Button>
                        </ProtectedSection>
                      )}
                    </>
                  ) : (
                    <>
                      <ProtectedSection code="CLIENT_MINOR_VIEW">
                        <Button
                          variant="outline-dark"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => handleViewMinor(row)}
                        >
                          Voir
                        </Button>
                      </ProtectedSection>

                      <ProtectedSection code="CLIENT_MINOR_UPDATE">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => handleMinorEdit(row)}
                        >
                          Modifier
                        </Button>
                      </ProtectedSection>

                      <ProtectedSection code="CLIENT_MINOR_DELETE">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="me-2 mb-1"
                          onClick={() => handleMinorDelete(row)}
                        >
                          Supprimer
                        </Button>
                      </ProtectedSection>

                      {row.active ? (
                        <ProtectedSection code="CLIENT_DEACTIVATE">
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="mb-1"
                            onClick={() => handleDeactivateMinor(row.id)}
                          >
                            Désactiver
                          </Button>
                        </ProtectedSection>
                      ) : (
                        <ProtectedSection code="CLIENT_ACTIVATE">
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="mb-1"
                            onClick={() => handleActivateMinor(row.id)}
                          >
                            Activer
                          </Button>
                        </ProtectedSection>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {total === 0 && <p className="text-center text-muted m-0">Aucun résultat.</p>}
        {total > 0 && renderPagination()}

        <ClientModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onSave={handleSave}
          initialData={editData || {}}
        />

        <ClientExtraFieldsModal
          show={showFieldsModal}
          onHide={() => setShowFieldsModal(false)}
        />

        <ClientMinorListModal
          show={minorsModal.show}
          onHide={() => setMinorsModal({ show: false, parent: null })}
          parent={minorsModal.parent}
        />

        <ClientMinorCreateFromPickerModal
          show={showCreateMinorModal}
          onHide={() => setShowCreateMinorModal(false)}
          mode="create"
          onSave={async ({ parentId, fields }) => {
            await clientMinorService.create(parentId, fields);
            setShowCreateMinorModal(false);
            fetch();
          }}
        />

        {minorEditPicker.show && (
          <ClientMinorCreateFromPickerModal
            show={true}
            onHide={() => setMinorEditPicker({ show: false, initial: null })}
            mode="edit"
            initialMinor={minorEditPicker.initial}
            onSave={handleMinorPickerSaveEdit}
          />
        )}

        {viewAdult && (
          <ClientViewModal
            show={true}
            onHide={() => setViewAdult(null)}
            client={viewAdult}
          />
        )}

        {viewMinor && (
          <ClientMinorViewModal
            show={true}
            onHide={() => setViewMinor(null)}
            minor={viewMinor}
          />
        )}

        <ClientFilesModal
          clientId={filesModal.clientId}
          show={filesModal.show}
          onHide={() => setFilesModal({ show: false, clientId: null })}
        />
      </Container>
    </>
  );
}